import type { ChatFileView } from "@beacon/core/chat"

/*
 * What a Member has written and not sent, kept on this device alone: the message half-written in a
 * conversation's box, or a thread's (a `Draft`), and the messages they sent that never had an
 * answer (`KeptUnsent`), so neither is lost to a reload, a dropped network or a walk to another
 * conversation. Nothing here goes to the web API or to another device, and nothing here is ever
 * sent by itself: a draft goes back in its box, and an unsent message back in the list, failed,
 * for the Member to send or discard.
 *
 * Each is kept under the Member and the place it was written (`DraftPlace`), so one Member never
 * reads another's, and a conversation never shows another's. What a Member may no longer read goes
 * with it: `forgetDrafts` when a conversation is known to be gone from them, `forgetThreadDraft`
 * when a thread is, `forgetAllDrafts` as they sign out, and `settleDrafts` as someone signs in,
 * which keeps only theirs, and only what is younger than a month.
 *
 * The device's storage may be missing, full or refused (a private window): then nothing is kept,
 * and writing goes on as before.
 */

/** Where something was written: a Member's message in a conversation, or a reply in its `thread`. */
export type DraftPlace = { memberId: string; conversationId: string; thread?: string | undefined }

/** A message half-written: its markdown and the files uploaded for it. */
export type Draft = { body: string; files: readonly ChatFileView[] }

/** A message the Member sent that never had an answer, kept to send again as the same operation. */
export type KeptUnsent = {
  operationId: string
  body: string
  files: readonly ChatFileView[]
  /** Whether it asked Beacon, so sending it again asks again rather than only sending it. */
  asks?: boolean
}

/** How long a draft is kept unopened before this device lets it go. */
export const draftsKeptFor = 30 * 24 * 60 * 60_000

/**
 * How long a draft keeps its files: the web API removes a file a week after it was uploaded unless
 * a message carries it, so a draft lets go of them a day before.
 */
export const draftFilesKeptFor = 6 * 24 * 60 * 60_000

const prefix = "beacon.chat."

/** The device's storage, or none where the browser has none or refuses it. */
function deviceStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

const keyOf = (kind: "draft" | "unsent", { memberId, conversationId, thread }: DraftPlace) =>
  `${prefix}${kind}.${memberId}.${conversationId}.${thread ?? "-"}`

/** The Member and conversation a key of ours names, none for anyone else's key. */
function placeOfKey(key: string) {
  if (!key.startsWith(prefix)) return undefined
  const [, memberId, conversationId] = key.slice(prefix.length).split(".")
  return memberId && conversationId ? { memberId, conversationId } : undefined
}

type Stored<T> = { at: number; value: T }

function read<T>(key: string, storage = deviceStorage()): Stored<T> | undefined {
  try {
    const raw = storage?.getItem(key)
    if (!raw) return undefined
    const stored = JSON.parse(raw) as Stored<T>
    return typeof stored?.at === "number" && stored.value !== undefined ? stored : undefined
  } catch {
    return undefined
  }
}

function write(key: string, value: unknown, at: number, storage = deviceStorage()) {
  try {
    if (value === undefined) storage?.removeItem(key)
    else storage?.setItem(key, JSON.stringify({ at, value }))
  } catch {
    // Full or refused: this device keeps nothing more, and nothing else changes.
  }
}

/** Every key of ours on the device. */
function ourKeys(storage = deviceStorage()) {
  const keys: string[] = []
  try {
    for (let index = 0; storage && index < storage.length; index++) {
      const key = storage.key(index)
      if (key?.startsWith(prefix)) keys.push(key)
    }
  } catch {
    // Refused: there is nothing of ours to find.
  }
  return keys
}

function removeAll(keys: readonly string[], storage = deviceStorage()) {
  for (const key of keys) write(key, undefined, 0, storage)
}

/** A draft as stored: each file with when it was first kept, which is about when it was uploaded. */
type StoredDraft = { body: string; files: (ChatFileView & { keptAt: number })[] }

/**
 * The draft kept for `place`, none when there is none or it is a month old. Files kept longer than
 * the web API holds them unsent are left out; a draft of nothing else is then none.
 */
export function readDraft(place: DraftPlace, now = Date.now()): Draft | undefined {
  const stored = read<StoredDraft>(keyOf("draft", place))
  if (!stored || now - stored.at > draftsKeptFor) return undefined
  const body = typeof stored.value.body === "string" ? stored.value.body : ""
  const files = (Array.isArray(stored.value.files) ? stored.value.files : [])
    .filter(({ keptAt }) => now - keptAt <= draftFilesKeptFor)
    .map(({ keptAt: _, ...file }) => file)
  return body.trim() === "" && files.length === 0 ? undefined : { body, files }
}

/** Keeps `draft` for `place`, or lets it go when it holds nothing. */
export function keepDraft(place: DraftPlace, draft: Draft, now = Date.now()) {
  const key = keyOf("draft", place)
  if (draft.body.trim() === "" && draft.files.length === 0) return write(key, undefined, now)
  const before = new Map(
    (read<StoredDraft>(key)?.value.files ?? []).map(({ id, keptAt }) => [id, keptAt]),
  )
  const files = draft.files.map((file) => ({ ...file, keptAt: before.get(file.id) ?? now }))
  write(key, { body: draft.body, files } satisfies StoredDraft, now)
}

/**
 * The messages kept for `place` that were sent without an answer, oldest first: none once a month
 * has passed since the latest of them.
 */
export function readUnsent(place: DraftPlace, now = Date.now()): KeptUnsent[] {
  const stored = read<KeptUnsent[]>(keyOf("unsent", place))
  if (!stored || now - stored.at > draftsKeptFor || !Array.isArray(stored.value)) return []
  return stored.value.flatMap(({ operationId, body, files, asks }) =>
    typeof operationId === "string" && typeof body === "string"
      ? [
          {
            operationId,
            body,
            files: Array.isArray(files) ? files : [],
            ...(asks === true && { asks }),
          },
        ]
      : [],
  )
}

/** Keeps `list` as what was sent from `place` without an answer, or lets it go when it is empty. */
export function keepUnsent(place: DraftPlace, list: readonly KeptUnsent[], now = Date.now()) {
  write(
    keyOf("unsent", place),
    list.length === 0
      ? undefined
      : list.map(({ operationId, body, files, asks }) => ({
          operationId,
          body,
          files,
          ...(asks && { asks }),
        })),
    now,
  )
}

/** Forgets everything written in the conversation, by anyone, in it and its threads. */
export function forgetDrafts(conversationId: string) {
  removeAll(ourKeys().filter((key) => placeOfKey(key)?.conversationId === conversationId))
}

/** Forgets the reply being written in one thread. */
export function forgetThreadDraft(place: DraftPlace & { thread: string }) {
  removeAll([keyOf("draft", place), keyOf("unsent", place)])
}

/** Forgets everything written on this device, as the Member signs out. */
export function forgetAllDrafts() {
  removeAll(ourKeys())
}

/**
 * Keeps only what `memberId`, now signed in, wrote, and only what is younger than a month: anyone
 * else's goes, so whoever signs in on a shared device never finds what another left unsent.
 */
export function settleDrafts(memberId: string, now = Date.now()) {
  removeAll(
    ourKeys().filter((key) => {
      if (placeOfKey(key)?.memberId !== memberId) return true
      const stored = read<unknown>(key)
      return !stored || now - stored.at > draftsKeptFor
    }),
  )
}
